/**
 * カスタムエレメントの基底クラスです。<br>
 * 継承したクラスに「属性名のキャメルケース」+"Changed"メソッドが存在する場合、その属性の値が変更された際に呼び出されます。<br>
 * connectedCallbackが呼ばれる前にattributeChangedCallbackが呼ばれた場合は変更内容を記録しておき、connectedCallback呼び出し時に呼び出します。<br>
 */
class KoyoriElement extends HTMLElement {

	/** スクロールバーの幅 */
	private static scrollBarSize: number;

	/**
	 * HTMLElement.children のラッパ関数です。forEachを使用できるよう配列として取得します。
	 */
	static children(element: HTMLElement): HTMLElement[] {
		let list: HTMLElement[] = [];
		Array.prototype.forEach.call(element.children, (child: HTMLElement) => {
			list.push(child);
		});
		return list;
	}

	/**
	 * HTMLElement.childNodes のラッパ関数です。forEachを使用できるよう配列として取得します。
	 */
	static childNodes(element: HTMLElement): HTMLElement[] {
		let list: HTMLElement[] = [];
		Array.prototype.forEach.call(element.childNodes, (child: HTMLElement) => {
			list.push(child);
		});
		return list;
	}

	/**
	 * HTMLElement.querySelectorAll のラッパ巻数です。forEachを使用できるよう配列として取得します。
	 */
	static querySelectorAll(element: HTMLElement, selector: string): HTMLElement[] {
		let list: HTMLElement[] = [];
		Array.prototype.forEach.call(element.querySelectorAll(selector), (child: HTMLElement) => {
			list.push(child);
		});
		return list;
	}

	/**
	 * イベントを発生させます。
	 * 
	 * @param element 対象エレメント
	 * @param eventType イベントタイプ
	 * @param canBubble バブリングの可不可
	 * @param cancelable キャンセルの可不可
	 * @param detail イベント詳細
	 * @return キャンセルされたか（Event.preventDefault()が呼び出されたか）
	 */
	static fireEvent(element: HTMLElement, eventType: string, canBubble = true, cancelable = true, detail?: any): boolean {
		let event = document.createEvent('CustomEvent');
		event.initCustomEvent(eventType, canBubble, cancelable, detail);
		return element.dispatchEvent(event);
	}

	/**
	 * キャメルケース文字列に変換します。
	 * 
	 * @param str キャメルケース文字列
	 */
	static toCamelCase(str: string): string {
		return str.replace(/[-_]+[a-zA-Z0-9]/g, (char: string) => {
			return char.substring(1).toUpperCase();
		});
	}

	/**
	 * スクロールバーの幅を取得します。
	 */
	static getScrollBarSize(): number {
		if (KoyoriElement.scrollBarSize) {
			return KoyoriElement.scrollBarSize;
		}
		let div = document.createElement('div');
		div.classList.add('koy-get-scroll-bar-size');
		document.body.appendChild(div);
		KoyoriElement.scrollBarSize = div.offsetWidth - div.clientWidth;
		document.body.removeChild(div);
		return KoyoriElement.scrollBarSize;
	}

	/**
	 * @param element paddingを除いた幅
	 */
	static getInnerWidth(element: HTMLElement): number {
		let style = getComputedStyle(element);
		return element.clientWidth - parseInt(style.paddingLeft) - parseInt(style.paddingRight);
	}

	/**
	 * @param element paddingを除いた高さ
	 */
	static getInnerHeight(element: HTMLElement): number {
		let style = getComputedStyle(element);
		let result = element.clientHeight - parseInt(style.paddingTop) - parseInt(style.paddingBottom);
		return result;
	}

	/**
	 * @param element marginを加えた幅
	 */
	static getOuterWidth(element: HTMLElement): number {
		let style = getComputedStyle(element);
		let result = element.offsetWidth + parseInt(style.marginLeft) + parseInt(style.marginRight);
		return result;
	}

	/**
	 * @param element marginを加えた高さ
	 */
	static getOuterHeight(element: HTMLElement): number {
		let style = getComputedStyle(element);
		let result = element.offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
		return result;
	}

	/**
	 * 他の兄弟エレメントを除く最大の高さを取得します。
	 * 
	 * @param element
	 */
	static getFitHeight(element: HTMLElement): number {
		let parent = element.parentElement;
		let targetStyle = getComputedStyle(element);
		let height = KoyoriElement.getInnerHeight(parent);
		element.style.display = 'block';
		KoyoriElement.children(parent).forEach(child => {
			if (child == element) {
				return;
			}
			let style = getComputedStyle(child);
			if (style.position == 'fixed' || style.position == 'absolute') {
				return;
			}
			if (style.display == 'block' || style.display == 'table' || style.display == 'flex' || targetStyle.display == 'block' || targetStyle.display == 'table' || targetStyle.display == 'flex') {
				height -= KoyoriElement.getOuterHeight(child);
			}
		});
		element.style.display = targetStyle.display;
		return height;
	}

	private connected = false;
	
	/** 変更イベントのスタック（準備完了時に呼び出す） */
	private changedEventStack: KoyoriAttributeChangedEvent[] = [];

	constructor() {
		super();
	}

	connectedCallback(): void {
		this.connected = true;
		this.callChangedEvents();
	}

	disconnectedCallback(): void {
		this.connected = false;
	}

	attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
		if (this.connected == false) {
			this.changedEventStack.push({
				attrName: attrName,
				oldVal: oldVal,
				newVal: newVal
			});
			return;
		}

		let self = this;
		let func = 'self.' + KoyoriElement.toCamelCase(attrName) + 'Changed';
		if (eval('typeof ' + func + ' == "function"')) {
			if (newVal == null) {
				eval(func + '(null)');
			} else {
				eval(func + '(' + JSON.stringify(newVal) + ')');
			}
		}
	}

	get observedAttributes(): string[] {
		return [];
	}
	
	private callChangedEvents() {
		this.changedEventStack.forEach(event => {
			this.attributeChangedCallback(event.attrName, event.oldVal, event.newVal);
		});
		this.changedEventStack = [];
	}

}

interface KoyoriAttributeChangedEvent {
	attrName: string,
	oldVal: string,
	newVal: string
}

